import { useEffect, useState } from 'react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { products } from '../../mocks/products';
import AnnouncementBar from './components/AnnouncementBar';
import Header from './components/Header';
import HeroSlider from './components/HeroSlider';
import CategoryTabs from './components/CategoryTabs';
import ProductSection from './components/ProductSection';
import FeaturesSection from './components/FeaturesSection';
import WhyChooseUs from './components/WhyChooseUs';
import Footer from './components/Footer';
import AnnouncementModal from './components/AnnouncementModal';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const homeReviews = [
  { author: '小明玩家', rating: 5, date: '2025-05-10', body: '超快速！付款後不到5分鐘就到帳了，以後都找laigame儲值！' },
  { author: 'GamerKing', rating: 5, date: '2025-05-15', body: '價格最便宜，服務也很好，客服秒回，強烈推薦！' },
  { author: '阿宅玩家', rating: 5, date: '2025-05-20', body: '用了好幾年了，從來沒出過問題，安全有保障，值得信賴。' },
  { author: '遊戲達人', rating: 5, date: '2025-06-01', body: '全站9折真的很划算，每次儲值都省不少錢，推推！' },
  { author: 'StarPlayer', rating: 5, date: '2025-06-05', body: '客服24小時都在，有問題馬上解決，非常專業！' },
  { author: '手遊愛好者', rating: 4, date: '2025-06-10', body: '整體服務很好，速度快、價格優，下次還會再來。' },
  { author: 'ProGamer99', rating: 5, date: '2025-06-12', body: '帳密閱後即焚的機制讓我很放心，安全性超高！' },
  { author: '台灣玩家', rating: 5, date: '2025-06-15', body: '原神儲值超快，比官方還便宜，以後都在這裡儲了！' },
];

export default function HomePage() {
  usePageMeta({
    title: 'laigame手遊代儲 | 專業遊戲儲值平台 - 安全快速便宜',
    description: 'laigame提供專業手遊代儲服務，支援原神、崩壞星穹鐵道等熱門遊戲儲值。24小時客服、超低折扣、5分鐘到帳，台灣最值得信賴的遊戲儲值平台。',
    keywords: '手遊代儲,遊戲儲值,原神儲值,崩壞星穹鐵道,遊戲點數,便宜儲值'
  });

  // Organization + WebSite Schema（從 index.html 移入，透過 VITE_SITE_URL 動態產生）
  useEffect(() => {
    const orgSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`,
          "name": "laigame手遊代儲",
          "alternateName": "laigame",
          "url": `${SITE_URL}/`,
          "logo": {
            "@type": "ImageObject",
            "url": `${SITE_URL}/vite.svg`,
            "width": 512,
            "height": 512
          },
          "description": "專業手遊代儲服務平台，提供原神、崩壞星穹鐵道、絕區零等熱門遊戲儲值，24小時全年無休，安全快速便宜。",
          "telephone": "+886-984-189784",
          "areaServed": "TW",
          "foundingDate": "2020",
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "telephone": "+886-984-189784",
              "contactType": "customer service",
              "availableLanguage": ["zh-TW", "zh-CN"],
              "areaServed": "TW",
              "contactOption": "TollFree"
            }
          ],
          "sameAs": [
            "https://line.me/R/ti/p/@laigame",
            "https://www.facebook.com/laigame"
          ]
        },
        {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`,
          "url": `${SITE_URL}/`,
          "name": "laigame手遊代儲",
          "description": "專業手遊代儲服務平台，安全快速便宜",
          "inLanguage": "zh-TW",
          "publisher": {
            "@id": `${SITE_URL}/#organization`
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${SITE_URL}/?q={search_term_string}`
            },
            "query-input": "required name=search_term_string"
          }
        }
      ]
    };

    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.id = 'home-org-website-schema';
    orgScript.text = JSON.stringify(orgSchema);
    document.head.appendChild(orgScript);

    return () => {
      const el = document.getElementById('home-org-website-schema');
      if (el) el.remove();
    };
  }, []);

  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);

  useEffect(() => {
    const hideForever = localStorage.getItem('hideAnnouncementModal');
    if (hideForever === 'true') return;
    const shownThisSession = sessionStorage.getItem('announcementModalShown');
    if (shownThisSession === 'true') return;
    const timer = setTimeout(() => {
      setShowAnnouncementModal(true);
      sessionStorage.setItem('announcementModalShown', 'true');
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // ItemList Schema — 熱門遊戲商品清單
    const topProducts = products.slice(0, 8);
    const itemListSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${SITE_URL}/#itemlist`,
      "name": "laigame熱門手遊代儲商品",
      "description": "laigame提供最優惠的手遊代儲服務，支援原神、崩壞星穹鐵道、絕區零等熱門遊戲",
      "url": SITE_URL,
      "numberOfItems": topProducts.length,
      "itemListElement": topProducts.map((p, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `${SITE_URL}/order`,
        "name": `${p.name} 代儲`,
        "item": {
          "@type": "Product",
          "name": `${p.name} 代儲服務`,
          "description": `${p.name}官方遊戲點數代儲，全站${p.discount}，安全快速`,
          "url": `${SITE_URL}/order`,
          "brand": {
            "@type": "Brand",
            "name": "laigame手遊代儲"
          },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "TWD",
            "price": p.denominations[0].price,
            "availability": "https://schema.org/InStock",
            "seller": {
              "@type": "Organization",
              "name": "laigame手遊代儲"
            }
          }
        }
      }))
    };

    const itemListScript = document.createElement('script');
    itemListScript.type = 'application/ld+json';
    itemListScript.id = 'home-itemlist-schema';
    itemListScript.text = JSON.stringify(itemListSchema);
    document.head.appendChild(itemListScript);

    return () => {
      const el = document.getElementById('home-itemlist-schema');
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    const totalRating = homeReviews.reduce((sum, r) => sum + r.rating, 0);
    const avgRating = (totalRating / homeReviews.length).toFixed(1);

    // LocalBusiness + AggregateRating + Review Schema
    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/#organization`,
      "name": "laigame手遊代儲",
      "url": SITE_URL,
      "logo": `${SITE_URL}/logo.png`,
      "description": "laigame提供專業手遊代儲服務，支援原神、崩壞星穹鐵道等熱門遊戲儲值。24小時客服、超低折扣、5分鐘到帳，台灣最值得信賴的遊戲儲值平台。",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": avgRating,
        "reviewCount": homeReviews.length,
        "bestRating": "5",
        "worstRating": "1"
      },
      "review": homeReviews.map(r => ({
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": r.author
        },
        "datePublished": r.date,
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": r.rating,
          "bestRating": "5",
          "worstRating": "1"
        },
        "reviewBody": r.body
      }))
    };

    // BreadcrumbList for homepage
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "首頁",
          "item": SITE_URL
        }
      ]
    };

    const ratingScript = document.createElement('script');
    ratingScript.type = 'application/ld+json';
    ratingScript.id = 'home-rating-schema';
    ratingScript.text = JSON.stringify(localBusinessSchema);
    document.head.appendChild(ratingScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'home-breadcrumb-schema';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      ['home-rating-schema', 'home-breadcrumb-schema'].forEach(sid => {
        const el = document.getElementById(sid);
        if (el) document.head.removeChild(el);
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <AnnouncementBar />
      <Header />
      <HeroSlider />
      <CategoryTabs />
      <ProductSection
        title="全部遊戲"
        products={products}
        icon="ri-fire-line"
        gradient="from-orange-500 to-red-500"
      />
      <FeaturesSection />
      <WhyChooseUs />
      <Footer />

      {showAnnouncementModal && (
        <AnnouncementModal onClose={() => setShowAnnouncementModal(false)} />
      )}
    </div>
  );
}